import { InvoiceLineItemType } from '@treadinc/horizon-api-spec';

import { AddOnCharge } from '~hooks/useAddOns/models';
import { SettlementLineItem } from '~hooks/useSettlementLineItems/models';

/**
 * Represents a grouped structure of settlement line items organized by Order → Job hierarchy.
 */
export interface OrderGroup {
  orderId: string;
  /** Display name for the order (friendlyOrderId, which is the order name) */
  displayName: string;
  jobs: JobGroup[];
  dispatchNumberName?: string | null;
  orderName?: string | null;
  projectName?: string | null;
  orderOrderId?: string | null;
  poJobNumber?: string | null;
}

export interface JobGroup {
  jobId: string;
  friendlyJobId: string;
  /** The first line item in the group, used to extract job-level data (date, truck, equipment) */
  firstLineItem: SettlementLineItem;
  /** Line items sorted: Freight → Material → Add-ons (FSC first, then alphabetical) */
  lineItems: SettlementLineItem[];
  /** Add-on charges associated with this job */
  addOnCharges: AddOnCharge[];
}

/**
 * Result of grouping line items by Order → Job hierarchy.
 */
interface GroupedLineItemsResult {
  /** Order groups with hierarchical structure */
  orderGroups: OrderGroup[];
  /** Add-on charges that are not associated with any job (settlement-level add-ons) */
  ungroupedAddOnCharges: AddOnCharge[];
}

/**
 * Determines the sort priority for a line item within a job group.
 * Returns:
 * - 0: Freight line items
 * - 1: Material line items
 * - 2+: Add-on services (FSC gets 2, others get 3+ alphabetically)
 */
function getLineItemSortPriority(lineItem: SettlementLineItem): number {
  // Freight line items come first
  if (
    lineItem.lineItemType === InvoiceLineItemType.FREIGHT_RATE_LINE_ITEM ||
    lineItem.lineItemType === InvoiceLineItemType.INVOICE_LINE_ITEM
  ) {
    return 0;
  }

  // Material line items come second
  if (lineItem.lineItemType === InvoiceLineItemType.MATERIAL_RATE_LINE_ITEM) {
    return 1;
  }

  // If there are services, this is an add-on line item
  if (lineItem.services.length > 0) {
    // Fuel surcharge add-ons come first among add-ons
    const hasFuelSurcharge = lineItem.services.some(
      (service) =>
        service.toLowerCase().includes('fuel') || service.toLowerCase().includes('fsc'),
    );
    if (hasFuelSurcharge) {
      return 2;
    }
    // Other add-ons come after fuel surcharge
    return 3;
  }

  // Default for unknown types
  return 4;
}

/**
 * Compares two line items for sorting within a job group.
 */
function compareLineItems(a: SettlementLineItem, b: SettlementLineItem): number {
  const priorityA = getLineItemSortPriority(a);
  const priorityB = getLineItemSortPriority(b);

  if (priorityA !== priorityB) {
    return priorityA - priorityB;
  }

  // For add-on items with equal priority (both non-FSC add-ons), sort alphabetically by service name
  if (priorityA >= 3 && a.services.length > 0 && b.services.length > 0) {
    const serviceA = a.services[0].toLowerCase();
    const serviceB = b.services[0].toLowerCase();
    return serviceA.localeCompare(serviceB);
  }

  return 0;
}

/**
 * Compares two add-on charges for sorting within a job group.
 * Fuel surcharge add-ons come first, then others alphabetically.
 */
function compareAddOnCharges(a: AddOnCharge, b: AddOnCharge): number {
  const aIsFsc =
    a.name.toLowerCase().includes('fuel') || a.name.toLowerCase().includes('fsc');
  const bIsFsc =
    b.name.toLowerCase().includes('fuel') || b.name.toLowerCase().includes('fsc');

  if (aIsFsc && !bIsFsc) return -1;
  if (!aIsFsc && bIsFsc) return 1;

  // Both are FSC or both are non-FSC, sort alphabetically
  return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
}

/**
 * Extracts the primary order ID and friendly order ID from a line item.
 * A line item can have multiple IDs if it spans multiple jobs/orders,
 * but we use the first one for grouping purposes.
 */
function extractPrimaryOrderInfo(
  lineItem: SettlementLineItem,
): { orderId: string; friendlyOrderId: string } | null {
  const firstId = lineItem.ids[0];
  if (!firstId) return null;

  return {
    orderId: firstId.orderId,
    friendlyOrderId: firstId.friendlyOrderId,
  };
}

/**
 * Extracts the primary job ID and friendly job ID from a line item.
 */
function extractPrimaryJobInfo(
  lineItem: SettlementLineItem,
): { jobId: string; friendlyJobId: string } | null {
  const firstId = lineItem.ids[0];
  if (!firstId) return null;

  return {
    jobId: firstId.jobId,
    friendlyJobId: firstId.friendlyJobId,
  };
}

/**
 * Groups settlement line items by Order → Job hierarchy.
 *
 * @param lineItems - Flat array of settlement line items
 * @param addOnCharges - Add-on charges to be integrated into job groups
 * @returns GroupedLineItemsResult with order groups and ungrouped add-ons
 */
export function groupLineItemsByOrderJob(
  lineItems: SettlementLineItem[],
  addOnCharges: AddOnCharge[] = [],
): GroupedLineItemsResult {
  // Group line items by order ID
  const orderMap = new Map<
    string,
    { friendlyOrderId: string; lineItems: SettlementLineItem[] }
  >();

  for (const lineItem of lineItems) {
    const orderInfo = extractPrimaryOrderInfo(lineItem);
    if (!orderInfo) continue;

    const existing = orderMap.get(orderInfo.orderId);
    if (existing) {
      existing.lineItems.push(lineItem);
    } else {
      orderMap.set(orderInfo.orderId, {
        friendlyOrderId: orderInfo.friendlyOrderId,
        lineItems: [lineItem],
      });
    }
  }

  // Separate add-on charges into grouped (with job) and ungrouped (without job)
  const addOnsByJobId = new Map<string, AddOnCharge[]>();
  const ungroupedAddOnCharges: AddOnCharge[] = [];

  for (const addOn of addOnCharges) {
    const jobId = addOn.job?.id;
    if (jobId) {
      const existing = addOnsByJobId.get(jobId);
      if (existing) {
        existing.push(addOn);
      } else {
        addOnsByJobId.set(jobId, [addOn]);
      }
    } else {
      // Add-on has no job association - goes to settlement-level add-ons
      ungroupedAddOnCharges.push(addOn);
    }
  }

  // Sort ungrouped add-ons (FSC first, then alphabetically)
  ungroupedAddOnCharges.sort(compareAddOnCharges);

  // Build order groups
  const orderGroups: OrderGroup[] = [];

  for (const [orderId, orderData] of orderMap) {
    // Group line items within this order by job ID
    const jobMap = new Map<
      string,
      { friendlyJobId: string; lineItems: SettlementLineItem[] }
    >();

    for (const lineItem of orderData.lineItems) {
      const jobInfo = extractPrimaryJobInfo(lineItem);
      if (!jobInfo) continue;

      const existing = jobMap.get(jobInfo.jobId);
      if (existing) {
        existing.lineItems.push(lineItem);
      } else {
        jobMap.set(jobInfo.jobId, {
          friendlyJobId: jobInfo.friendlyJobId,
          lineItems: [lineItem],
        });
      }
    }

    // Build job groups
    const jobGroups: JobGroup[] = [];

    for (const [jobId, jobData] of jobMap) {
      // Sort line items within the job
      const sortedLineItems = [...jobData.lineItems].sort(compareLineItems);

      // Get add-on charges for this job and sort them
      const jobAddOnCharges = (addOnsByJobId.get(jobId) ?? []).sort(compareAddOnCharges);

      jobGroups.push({
        jobId,
        friendlyJobId: jobData.friendlyJobId,
        firstLineItem: sortedLineItems[0],
        lineItems: sortedLineItems,
        addOnCharges: jobAddOnCharges,
      });
    }

    // Sort job groups by date (using first line item's date)
    jobGroups.sort((a, b) => {
      const dateA = a.firstLineItem.date.valueOf();
      const dateB = b.firstLineItem.date.valueOf();
      return dateA - dateB;
    });

    orderGroups.push({
      orderId,
      displayName: orderData.friendlyOrderId,
      jobs: jobGroups,
    });
  }

  // Sort order groups by the earliest job date within each order
  orderGroups.sort((a, b) => {
    const dateA = a.jobs[0]?.firstLineItem.date.valueOf() ?? 0;
    const dateB = b.jobs[0]?.firstLineItem.date.valueOf() ?? 0;
    return dateA - dateB;
  });

  return {
    orderGroups,
    ungroupedAddOnCharges,
  };
}
